import { useEffect, useMemo, useState } from "react";
import { Button, Input, InputNumber, Modal, Select, Spin, Table, message } from "antd";
import PageLayout from "@layouts/PageLayout";
import { useAppSelector } from "@store/hooks";
import { cancelOrder, formatInr, getAllOrders, refundOrder, updateOrderAddress, updateOrderStatus, updateRefundStatus } from "@services/shop.service";
import AdminFrame from "./AdminFrame";
import { matchesQuery, PAGE_SIZE } from "./list";
import OrderJourney from "./OrderJourney";
import "./shop.css";

function AdminOrdersPage() {
  const role = useAppSelector((state) => state.auth.user?.role || "user");
  const superAdmin = role === "admin" || role === "superadmin";
  const [orders, setOrders] = useState<any[]>([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState<any>(null);
  const [refundTarget, setRefundTarget] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [reference, setReference] = useState("");
  const [forceTarget, setForceTarget] = useState<any>(null);
  const [forceStatus, setForceStatus] = useState("packed");
  const [shipTarget, setShipTarget] = useState<any>(null);
  const [trackingId, setTrackingId] = useState("");
  const [carrier, setCarrier] = useState("");
  const [saving, setSaving] = useState(false);
  const [lineQty, setLineQty] = useState<Record<string, number>>({});
  const [addressOrder, setAddressOrder] = useState<any>(null);

  const load = () => {
    setLoading(true);
    getAllOrders()
      ?.then((data) => setOrders(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () =>
      orders.filter((order) =>
        matchesQuery(query, [
          order.shipping_name,
          order.customer_email,
          order.customer_name,
          order.phone,
          order.order_number,
          order.status,
          order.status_label,
          order.tracking_id,
          order.id,
        ]),
      ),
    [orders, query],
  );

  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const moveStatus = async (record: any, status: string) => {
    if (status === record.status) return;
    if (status === "cancelled") {
      setCancelTarget(record);
      setReason("");
      return;
    }
    if (["shipped", "in_transit", "out_for_delivery"].includes(status)) {
      setShipTarget({ ...record, nextStatus: status });
      setTrackingId(record.tracking_id || "");
      setCarrier(record.carrier || "");
      setReason("");
      return;
    }
    await updateOrderStatus(record.id, { status });
    message.success("Shipment status updated");
    load();
  };

  return (
    <PageLayout>
      <AdminFrame
        title="All orders"
        meta={superAdmin ? `${filtered.length} orders · superadmin` : `${filtered.length} orders · shipment status only`}
        page={page}
        total={filtered.length}
        onPage={setPage}
        extra={
          <Input.Search
            allowClear
            placeholder="Search name, email, or status"
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
        }
      >
        <Table
          className="data-table"
          rowKey="id"
          loading={loading}
          dataSource={pageItems}
          expandable={{
            expandedRowRender: (record) => (
              <div>
                <OrderJourney order={record} />
                {(record.items || []).map((item: any) => {
                  const active = item.active_quantity ?? item.quantity;
                  return (
                    <div key={item.id} className="order-item-row">
                      <span className="muted">
                        {active} of {item.quantity} × {item.name}
                        {item.cancelled_quantity ? ` · ${item.cancelled_quantity} cancelled` : ""}
                      </span>
                      {superAdmin && record.actions?.can_cancel_items && active > 0 ? (
                        <InputNumber
                          min={0}
                          max={active}
                          value={lineQty[item.id] || 0}
                          onChange={(value) => setLineQty((current) => ({ ...current, [item.id]: Number(value || 0) }))}
                        />
                      ) : null}
                    </div>
                  );
                })}
                <div className="order-actions">
                  {record.tracking_id ? <span className="muted">{record.carrier || "Courier"} · {record.tracking_id}</span> : null}
                  {record.actions?.can_cancel ? (
                    <Button onClick={() => { setCancelTarget(record); setReason(""); }}>Cancel</Button>
                  ) : record.actions?.can_cancel_items ? null : (
                    <span className="muted">{record.actions?.cancel_block_reason}</span>
                  )}
                  {superAdmin && record.actions?.can_cancel_items ? (
                    <Button
                      onClick={() => {
                        const items = (record.items || [])
                          .map((item: any) => ({ item_id: item.id, quantity: Number(lineQty[item.id] || 0) }))
                          .filter((item: any) => item.quantity > 0);
                        if (!items.length) {
                          message.warning("Choose how many units to cancel");
                          return;
                        }
                        setCancelTarget({ ...record, partialItems: items });
                        setReason("");
                      }}
                    >
                      Cancel selected
                    </Button>
                  ) : null}
                  {superAdmin && record.actions?.can_refund ? (
                    <Button
                      onClick={() => {
                        setRefundTarget(record);
                        setRefundAmount(String(record.actions.refundable_amount || ""));
                        setReference("");
                        setReason("");
                      }}
                    >
                      Refund {formatInr(record.actions.refundable_amount)}
                    </Button>
                  ) : record.actions?.refund_block_reason ? (
                    <span className="muted">{record.actions.refund_block_reason}</span>
                  ) : null}
                  {superAdmin && record.actions?.can_set_refund_status ? (
                    <Select
                      value={record.refund_status && record.refund_status !== "none" ? record.refund_status : undefined}
                      placeholder="Refund status"
                      style={{ width: 200 }}
                      options={[
                        { value: "eligible", label: "Refund eligible" },
                        { value: "initiated", label: "Refund initiated" },
                        { value: "processing", label: "Refund processing" },
                        { value: "partially_refunded", label: "Partially refunded" },
                        { value: "refunded", label: "Refunded" },
                        { value: "not_received", label: "Refund not received" },
                        { value: "failed", label: "Refund failed" },
                      ]}
                      onChange={async (status) => {
                        await updateRefundStatus(record.id, {
                          status,
                          note: status === "not_received" ? "Customer did not receive the refund" : undefined,
                        });
                        message.success("Refund status updated");
                        load();
                      }}
                    />
                  ) : null}
                  {superAdmin && record.actions?.can_change_address ? (
                    <Button
                      onClick={() =>
                        setAddressOrder({
                          id: record.id,
                          shipping_name: record.shipping_name || "",
                          phone: record.phone || "",
                          address: record.address || "",
                          city: record.city || "",
                          pincode: record.pincode || "",
                        })
                      }
                    >
                      Change address
                    </Button>
                  ) : null}
                  {superAdmin ? (
                    <Button type="link" onClick={() => { setForceTarget(record); setReason(""); setForceStatus(record.status); }}>
                      Correct status
                    </Button>
                  ) : null}
                </div>
              </div>
            ),
          }}
          locale={{ emptyText: loading ? <Spin /> : query.trim() ? "No orders match that search" : "No orders yet" }}
          pagination={false}
          scroll={{ x: 980 }}
          columns={[
            { title: "Order ID", dataIndex: "order_number", render: (value) => value || "—" },
            { title: "Email", dataIndex: "customer_email" },
            { title: "Phone", dataIndex: "phone" },
            { title: "Total", dataIndex: "total", render: (value) => formatInr(value) },
            {
              title: "Items",
              render: (_, record: any) =>
                (record.items || []).map((item: any) => `${item.quantity}× ${item.name}`).join(", "),
            },
            {
              title: "Status",
              render: (_, record: any) => {
                const next = record.actions?.next_statuses || [];
                const options = [
                  { value: record.status, label: record.status_label || record.status },
                  ...next
                    .filter((step: any) => step.status !== record.status)
                    .map((step: any) => ({ value: step.status, label: step.label })),
                ];
                return (
                  <Select
                    value={record.status}
                    style={{ width: 180 }}
                    options={options}
                    disabled={options.length < 2}
                    onChange={(status) => moveStatus(record, status)}
                  />
                );
              },
            },
            {
              title: "Placed",
              dataIndex: "created_at",
              render: (value) => new Date(value).toLocaleString(),
            },
          ]}
        />
      </AdminFrame>
      <Modal
        title="Update shipment"
        open={Boolean(shipTarget)}
        confirmLoading={saving}
        onCancel={() => setShipTarget(null)}
        onOk={async () => {
          setSaving(true);
          try {
            await updateOrderStatus(shipTarget.id, {
              status: shipTarget.nextStatus,
              note: reason || undefined,
              tracking_id: trackingId || undefined,
              carrier: carrier || undefined,
            });
            message.success("Shipment status updated");
            setShipTarget(null);
            load();
          } finally {
            setSaving(false);
          }
        }}
      >
        <Input style={{ marginBottom: 8 }} value={carrier} placeholder="Courier, for example Delhivery" onChange={(event) => setCarrier(event.target.value)} />
        <Input style={{ marginBottom: 8 }} value={trackingId} placeholder="Tracking id" onChange={(event) => setTrackingId(event.target.value)} />
        <Input.TextArea rows={3} value={reason} placeholder="Note for the timeline" onChange={(event) => setReason(event.target.value)} />
      </Modal>
      <Modal
        title="Cancel order"
        open={Boolean(cancelTarget)}
        confirmLoading={saving}
        okButtonProps={{ disabled: reason.trim().length < 3 }}
        onCancel={() => setCancelTarget(null)}
        onOk={async () => {
          setSaving(true);
          try {
            await cancelOrder(
              cancelTarget.id,
              cancelTarget.partialItems ? { reason, items: cancelTarget.partialItems } : { reason },
            );
            message.success(cancelTarget.partialItems ? "Items cancelled" : "Order cancelled");
            setCancelTarget(null);
            load();
          } finally {
            setSaving(false);
          }
        }}
      >
        <Input.TextArea rows={3} value={reason} placeholder="Reason" onChange={(event) => setReason(event.target.value)} />
      </Modal>
      <Modal
        title="Refund order"
        open={Boolean(refundTarget)}
        confirmLoading={saving}
        okText="Refund"
        onCancel={() => setRefundTarget(null)}
        onOk={async () => {
          setSaving(true);
          try {
            await refundOrder(refundTarget.id, {
              amount: refundAmount ? Number(refundAmount) : undefined,
              note: reason || undefined,
              ...(reference.trim()
                ? { reference: reference.trim(), mark_completed: true }
                : {}),
            });
            message.success("Refund recorded");
            setRefundTarget(null);
            load();
          } finally {
            setSaving(false);
          }
        }}
      >
        <p className="muted">Leave the reference empty to send the refund through Razorpay. Add a reference if the money was already returned.</p>
        <Input
          style={{ marginBottom: 8 }}
          value={refundAmount}
          placeholder="Amount"
          onChange={(event) => setRefundAmount(event.target.value)}
        />
        <Input
          style={{ marginBottom: 8 }}
          value={reference}
          placeholder="Refund reference"
          onChange={(event) => setReference(event.target.value)}
        />
        <Input.TextArea rows={3} value={reason} placeholder="Note" onChange={(event) => setReason(event.target.value)} />
      </Modal>
      <Modal
        title="Correct shipment status"
        open={Boolean(forceTarget)}
        confirmLoading={saving}
        okButtonProps={{ disabled: reason.trim().length < 3 || forceStatus === forceTarget?.status }}
        onCancel={() => setForceTarget(null)}
        onOk={async () => {
          setSaving(true);
          try {
            await updateOrderStatus(forceTarget.id, { status: forceStatus, force: true, note: reason });
            message.success("Status corrected");
            setForceTarget(null);
            load();
          } finally {
            setSaving(false);
          }
        }}
      >
        <Select
          style={{ width: "100%", marginBottom: 8 }}
          value={forceStatus}
          onChange={setForceStatus}
          options={[
            "packed",
            "shipped",
            "in_transit",
            "out_for_delivery",
            "delivered",
            "delivery_failed",
            "return_requested",
            "return_in_transit",
            "returned",
          ].map((status) => ({ value: status, label: status.split("_").join(" ") }))}
        />
        <Input.TextArea rows={3} value={reason} placeholder="Why this correction is needed" onChange={(event) => setReason(event.target.value)} />
      </Modal>
      <Modal
        title="Change delivery address"
        open={Boolean(addressOrder)}
        confirmLoading={saving}
        onCancel={() => setAddressOrder(null)}
        onOk={async () => {
          setSaving(true);
          try {
            await updateOrderAddress(addressOrder.id, addressOrder);
            message.success("Address updated");
            setAddressOrder(null);
            load();
          } finally {
            setSaving(false);
          }
        }}
        okButtonProps={{
          disabled: !addressOrder?.shipping_name?.trim() || !addressOrder?.phone?.trim() || !addressOrder?.address?.trim(),
        }}
      >
        <div style={{ display: "grid", gap: 8 }}>
          <Input placeholder="Name" value={addressOrder?.shipping_name} onChange={(event) => setAddressOrder((current: any) => ({ ...current, shipping_name: event.target.value }))} />
          <Input placeholder="Phone" value={addressOrder?.phone} onChange={(event) => setAddressOrder((current: any) => ({ ...current, phone: event.target.value }))} />
          <Input.TextArea rows={2} placeholder="Address" value={addressOrder?.address} onChange={(event) => setAddressOrder((current: any) => ({ ...current, address: event.target.value }))} />
          <Input placeholder="City" value={addressOrder?.city} onChange={(event) => setAddressOrder((current: any) => ({ ...current, city: event.target.value }))} />
          <Input placeholder="Pincode" value={addressOrder?.pincode} onChange={(event) => setAddressOrder((current: any) => ({ ...current, pincode: event.target.value }))} />
        </div>
      </Modal>
    </PageLayout>
  );
}

export default AdminOrdersPage;
